<template>
    <div class="layout-picker" @click.stop>
        <button
            type="button"
            class="picker-trigger"
            :class="{ 'picker-trigger--active': isActive }"
            :aria-expanded="open"
            aria-label="Формат ответа"
            @click.stop="toggle"
        >
            <ChatIcon name="layout" :size="14" />
            <span class="picker-label">{{ currentTitle }}</span>
        </button>

        <div v-if="open" class="picker-popup" @click.stop>
            <div class="popup-head">
                <span class="popup-title">Формат ответа</span>
                <span class="popup-sub">
                    Можно совместить до {{ maxSelected }} шаблонов — ответ соберётся как лист дашборда
                </span>
            </div>

            <p v-if="error" class="popup-error">{{ error }}</p>
            <p v-else-if="!layouts.length" class="popup-error">Загрузка шаблонов…</p>

            <div class="popup-grid">
                <button
                    v-for="layout in layouts"
                    :key="layout.id"
                    type="button"
                    class="layout-card"
                    :class="{
                        'layout-card--selected': isSelected(layout.id),
                        'layout-card--disabled': isBlocked(layout.id),
                    }"
                    @click.stop="toggleLayout(layout.id)"
                >
                    <span class="layout-mark" :class="{ 'layout-mark--on': isSelected(layout.id) }">
                        <ChatIcon v-if="isSelected(layout.id)" name="check" :size="10" />
                    </span>
                    <ChatLayoutSkeleton :kind="layout.skeleton || layout.id" />
                    <span class="layout-name">{{ layout.title }}</span>
                    <span class="layout-when">{{ layout.when_to_use || layout.subtitle }}</span>
                </button>
            </div>

            <p v-if="isBlockedHintVisible" class="popup-error popup-error--hint">
                Достигнут предел в {{ maxSelected }} шаблона: снимите один, чтобы выбрать другой.
            </p>
        </div>
    </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import ChatIcon from '@/components/chat/icons/ChatIcon.vue'
import ChatLayoutSkeleton from './ChatLayoutSkeleton.vue'
import { getChatResponseFormatCatalog } from '@/utils/chatApi'

const props = defineProps({
    // Список id выбранных шаблонов. Пустой = «Авто».
    modelValue: { type: Array, default: () => [] },
})

const emit = defineEmits(['update:modelValue'])

const open = ref(false)
const layouts = ref([])
const defaultId = ref('auto')
const maxSelected = ref(3)
const error = ref('')

const selected = computed(() => (Array.isArray(props.modelValue) ? props.modelValue : []))

const currentTitle = computed(() => {
    if (!selected.value.length) return 'Формат'
    const titles = selected.value
        .map(id => layouts.value.find(layout => layout.id === id)?.title)
        .filter(Boolean)
    if (!titles.length) return 'Формат'
    return titles.length === 1 ? titles[0] : `${titles[0]} +${titles.length - 1}`
})

const isActive = computed(() => selected.value.length > 0)

// Подсказку про лимит показываем только когда он реально достигнут.
const isBlockedHintVisible = computed(() => selected.value.length >= maxSelected.value)

function isSelected(id) {
    return selected.value.includes(id)
}

function isBlocked(id) {
    return !isSelected(id) && id !== defaultId.value && selected.value.length >= maxSelected.value
}

function toggle() {
    open.value = !open.value
}

function toggleLayout(id) {
    // «Авто» — это отсутствие выбора, поэтому его карточка просто сбрасывает всё.
    if (id === defaultId.value) {
        emit('update:modelValue', [])
        open.value = false
        return
    }

    if (isSelected(id)) {
        emit('update:modelValue', selected.value.filter(item => item !== id))
        return
    }
    if (isBlocked(id)) return

    // Порядок храним как в каталоге: так собранный ответ читается одинаково
    // независимо от того, в какой последовательности их нажали.
    const next = [...selected.value, id]
    const order = layouts.value.map(layout => layout.id)
    emit('update:modelValue', next.sort((a, b) => order.indexOf(a) - order.indexOf(b)))
}

// Закрытие по клику снаружи — тем же приёмом, что фильтры инпута: слушаем
// pointerdown на документе и сравниваем цель с корнем компонента.
function onDocumentPointerDown(event) {
    if (!open.value) return
    if (event.target.closest?.('.layout-picker')) return
    open.value = false
}

onMounted(async () => {
    document.addEventListener('pointerdown', onDocumentPointerDown)
    try {
        const data = await getChatResponseFormatCatalog()
        layouts.value = Array.isArray(data?.layouts) ? data.layouts : []
        if (typeof data?.default === 'string') defaultId.value = data.default
        if (Number.isFinite(data?.max_selected)) maxSelected.value = data.max_selected
    } catch (err) {
        error.value = 'Не удалось загрузить шаблоны ответа'
    }
})

onBeforeUnmount(() => {
    document.removeEventListener('pointerdown', onDocumentPointerDown)
})

defineExpose({ close: () => { open.value = false } })
</script>

<style scoped>
.layout-picker {
    position: relative;
    display: inline-flex;
}

.picker-trigger {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: transparent;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    padding: 4px 8px;
    font-family: inherit;
    font-size: 11.5px;
    color: #5f5f5f;
    cursor: pointer;
    white-space: nowrap;
}

.picker-trigger:hover {
    background: #f0f0f0;
    border-color: #c0c0c0;
}

.picker-trigger--active {
    border-color: #E30613;
    color: #E30613;
}

.picker-label {
    max-width: 110px;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* Попап раскрывается вверх: инпут прижат к низу экрана. */
.picker-popup {
    position: absolute;
    right: 0;
    bottom: calc(100% + 8px);
    width: min(520px, calc(100vw - 48px));
    max-height: 60vh;
    overflow-y: auto;
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 10px;
    box-shadow: 0 12px 32px rgba(21, 26, 34, 0.14);
    padding: 14px;
    z-index: 30;
}

.popup-head {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-bottom: 10px;
}

.popup-title {
    font-size: 12.5px;
    font-weight: 700;
    color: #23282C;
}

.popup-sub {
    font-size: 10.5px;
    color: #888888;
}

.popup-error {
    font-size: 11.5px;
    color: #888888;
    margin: 0 0 8px;
}

.popup-error--hint {
    margin: 8px 0 0;
}

.popup-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 8px;
}

.layout-card {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 6px;
    text-align: left;
    background: white;
    border: 1px solid #e7e7e7;
    border-radius: 8px;
    padding: 8px;
    font-family: inherit;
    cursor: pointer;
}

.layout-card--disabled {
    opacity: 0.45;
    cursor: not-allowed;
}

.layout-mark {
    position: absolute;
    top: 6px;
    right: 6px;
    width: 15px;
    height: 15px;
    border: 1px solid #d6d5d1;
    border-radius: 4px;
    background: white;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: white;
}

.layout-mark--on {
    background: #E30613;
    border-color: #E30613;
}

.layout-card:hover {
    border-color: #c0c0c0;
    background: #fbfbfa;
}

.layout-card--selected {
    border-color: #E30613;
    box-shadow: inset 0 0 0 1px #E30613;
}

.layout-name {
    font-size: 12px;
    font-weight: 600;
    color: #23282C;
}

.layout-when {
    font-size: 10.5px;
    line-height: 1.4;
    color: #888888;
}

@media (max-width: 600px) {
    .picker-label { display: none; }
    .popup-grid { grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); }
}
</style>
